define('appkit/controller',['require','exports','module','underscore','backbone','marionette','backbone.radio','bluebird','./util/key.prefixer','./resource.manager'],function(require, exports, module) {
  var _ = require('underscore');
  var Backbone = require('backbone');
  var Mn = require('marionette');
  var Radio = require('backbone.radio');
  var Promise = require('bluebird');
  var KeyPrefixer = require('./util/key.prefixer');
  var ResourceManager = require('./resource.manager');

  var modelsChannel = Radio.channel('models');

  var Controller = Mn.Object.extend({
    constructor: function(options) {
      this.options = _.extend({}, _.result(this, 'options'), options);
      this.mergeOptions(this.options, ['app', 'region', 'state']);

      this.startInterceptors = [];

      this.once(
        'destroy',
        function() {
          this.state.stopListening();
        },
        this
      );

      Radio.request('AppKit.controllerService', 'setCurrentController', this);

      this.initializeState();
      this.initializeResourceManager();
      this.initializeResources();
      this.initializePageView();

      this.initialize.apply(this, arguments);
    },

    resourceEventPrefix: 'resources:',

    registerStartInterceptor: function(callback, context) {
      this.startInterceptors.push({
        callback: callback || function() {},
        context: context || this,
      });
      return this;
    },

    initializeState: function() {
      var stateConfiguration = Mn._getValue(this.stateConfiguration, this) || {};
      var stateDefaults = Mn._getValue(stateConfiguration.defaults, this);

      if (stateDefaults) {
        this.state.set(stateDefaults);
      }
    },

    initializeResourceManager: function() {
      this.resourceManager = new ResourceManager();
      this.once('start', function() {
        // this forces the state resources manager
        // to begin searching for page completion
        // thus causing loaded, error, etc.
        // to be triggered
        this.resourceManager.start();
      })
        .once('destroy', function() {
          this.resourceManager.destroy();
        })
        .listenTo(this.resourceManager, 'all', function(eventName) {
          var args = _.rest(arguments);
          this.triggerMethod.apply(this, [this.resourceEventPrefix + eventName].concat(args));
        })
        .on('resource:initialize', function(self, resourceInstance, options) {
          var resourceDefinition = _.result(options, 'resourceDefinition') || {};
          var shouldFetchOnStart = _.result(resourceDefinition, 'fetchOnStart') === true;
          var shouldFetchWhenNew = _.result(resourceDefinition, 'fetchWhenNew') == true;

          if (_.result(resourceDefinition, 'required')) {
            this.resourceManager.addDependency(resourceInstance);
          } else {
            this.resourceManager.addResource(resourceInstance);
          }

          this.on('start', function() {
            if (shouldFetchOnStart) {
              if (resourceInstance instanceof Backbone.Model) {
                if (!resourceInstance.isNew() || shouldFetchWhenNew) {
                  resourceInstance.fetch();
                }
              } else if (resourceInstance instanceof Backbone.Collection) {
                resourceInstance.fetch();
              }
            }
            // tell datakit to enable relationship lazy loading for this resource
            Radio.request('DataKit', 'enableRelationshipLazyLoading', resourceInstance);
          });
        });
    },

    initializePageView: function() {
      this.page = this.buildPageView();

      if (!this.page) {
        throw new Error('No page could be created for this controller');
      }
    },

    initializeResources: function() {
      var resources = Mn._getValue(this.resources, this);
      var defaults = Mn._getValue(this.resourceDefaults, this);

      this.resources = {};

      _.each(
        resources,
        function(definition, resourceName) {
          this.initializeResource(resourceName, definition, defaults);
        },
        this
      );
    },

    initializeResource: function(resourceName, definition, defaults) {
      var entityClass = null;
      var instance = null;
      var isCollection = false;

      definition = _.extend({}, defaults, definition);

      if (definition.resourcePath) {
        var parsedPath = this.parseResourcePath(definition.resourcePath);
        var hasResource = Radio.request('AppKit.resourceService', 'hasResource', parsedPath.resourceName);
        if (!hasResource) {
          this.once('resource:initialize:' + parsedPath.resourceName, function() {
            this.initializeResource(resourceName, definition, defaults);
          });
          return null;
        }
        instance = Radio.request('AppKit.resourceService', 'resource', parsedPath.resourcePath);
        if (!instance) {
          return null;
        }
      } else if (definition.collection || definition.collectionClass) {
        entityClass = this.getResourceClass(definition.collection || definition.collectionClass);
        if (!entityClass) {
          if (_.isString(definition.collection || definition.collectionClass)) {
            throw new Error(
              'Could not find a resource collection by the name "' +
                (definition.collection || definition.collectionClass) +
                '".'
            );
          } else {
            throw new Error('Could not find a resource collection.');
          }
        }
        instance = this.buildCollection(
          resourceName,
          entityClass,
          this.buildCollectionData(resourceName, definition),
          this.buildResourceOptions(resourceName, definition)
        );
      } else {
        entityClass = this.getResourceClass(definition.model || definition.modelClass);
        if (!entityClass) {
          if (_.isString(definition.model || definition.modelClass)) {
            throw new Error(
              'Could not find a resource model by the name "' + (definition.model || definition.modelClass) + '"'
            );
          } else {
            throw new Error('Could not find a resource model');
          }
        }
        instance = this.buildModel(
          resourceName,
          entityClass,
          this.buildModelAttributes(resourceName, definition),
          this.buildResourceOptions(resourceName, definition)
        );
      }

      this.setResource(resourceName, instance);

      this.triggerMethod('resource:initialize', this, instance, {
        resourceName: resourceName,
        resourceDefinition: definition,
      });
      this.triggerMethod('resource:initialize:' + resourceName, this, instance, {
        resourceName: resourceName,
        resourceDefinition: definition,
      });

      return instance;
    },

    parseResourcePath: function(resourcePath) {
      if (!_.isString(resourcePath)) {
        return {};
      }
      var parts = resourcePath.split('.');
      var resourceName = parts[0];
      if (resourceName == '@resources') {
        // handling case when resourcePath is @resources.resourceName
        resourceName = parts[1];
        parts = _.rest(parts);
      }
      return {
        resourceName: resourceName,
        dataPath: _.rest(parts).join('.'),
        resourcePath: parts.join('.'),
      };
    },

    getResourceClass: function(resourceClass) {
      if (_.isString(resourceClass)) {
        // resourceClass cloud be Model.Collection
        var parts = resourceClass.split('.');
        // will always be the model part
        var firstPart = parts[0];
        // could the Collection part
        var remainingParts = _.rest(parts);
        // resolve the resource class from the models channel
        resourceClass = modelsChannel.request(firstPart);
        while (resourceClass && remainingParts.length > 0) {
          resourceClass = resourceClass[remainingParts.shift()];
        }
      }
      return resourceClass;
    },

    buildModelAttributes: function(name, definition) {
      return this.resolveObjectWithStateVariables(
        Mn._getValue(definition.attrs, this) || Mn._getValue(definition.data, this)
      );
    },

    buildModel: function(name, modelClass, attrs, options) {
      return new modelClass(attrs, options);
    },

    buildCollectionData: function(name, definition) {
      return _.each(
        Mn._getValue(definition.data, this),
        function(attrs) {
          return this.resolveObjectWithStateVariables(attrs);
        },
        this
      );
    },

    buildResourceOptions: function(resourceName, definition) {
      var options = Mn._getValue(definition.resourceOptions, this, [resourceName, definition]);
      return this.resolveObjectWithStateVariables(options);
    },

    resolveObjectWithStateVariables: function(obj) {
      return KeyPrefixer.resolve(
        obj,
        {
          '@state.': function(stateKey) {
            return this.state.get(stateKey);
          },
        },
        this
      );
    },

    buildCollection: function(name, collectionClass, data, options) {
      return new collectionClass(data || [], options);
    },

    getResource: function(name) {
      return Radio.request('AppKit.resourceService', 'resource', name);
    },

    setResource: function(name, resource) {
      // this is kept in for being able to iterate through resources
      // this functionality should be migrated to a public method
      this.resources[name] = resource;
      Radio.request('AppKit.resourceService', 'registerResource', name, resource, {
        owner: this,
      });
      this.on('destroy', function() {
        Radio.request('AppKit.resourceService', 'deregisterResource', name, {
          owner: this,
        });
      });
      return this;
    },

    eachResource: function(fn, context) {
      _.each(this.resources, fn, context);
      return this;
    },

    fetchResource: function(name, options) {
      var resource = Radio.request('AppKit.resourceService', 'resource', name);
      if (resource) {
        return resource.fetch(options);
      }
    },

    getPageView: function() {
      return this.pageView;
    },

    buildPageView: function() {
      var PageView = this.getPageView();

      if (!PageView) {
        return;
      }

      var pageViewOptions = KeyPrefixer.resolve(
        Mn._getValue(this.pageViewOptions, this),
        {
          '@resources.': function(resourceName) {
            return this.getResource(resourceName);
          },
        },
        this
      );

      return new PageView(pageViewOptions);
    },

    start: function() {
      if (this.didStart) {
        return this;
      }
      this.didStart = true;
      this.whenReadyToStart()
        .bind(this)
        .tap(function() {
          this.triggerMethod('before:start', this);
          this.listenToOnce(this.resourceManager, 'loaded', function() {
            try {
              this.region.show(this.page);
              this.trigger('started', this);
            } catch (e) {
              this.trigger('error', this, e);
              console.log('controller error: ', e);
              Promise.reject(e);
            }
          });
          this.listenToOnce(this.resourceManager, 'error', function(e, xhr) {
            console.log('controller error: ', e);
            this.trigger('error', this, e);
          });
          this.triggerMethod('start', this);
        })
        .catch(function(e) {
          console.log('controller error: ', e);
          this.trigger('error', this, e);
        });
      return this;
    },

    whenReadyToStart: function() {
      if (!this.didStart) {
        return Promise.reject(new Error('Controller has not started yet.'));
      }
      var controller = this;
      var startInterceptors = this.startInterceptors;
      this.startInterceptors = [];
      return Promise.each(startInterceptors, function(handler) {
        return handler.callback.call(handler.context, controller);
      });
    },

    stop: function() {
      if (this.didStart) {
        this.triggerMethod('before:stop', this);
        this.triggerMethod('stop', this);
      }
      this.destroy();
      return this;
    },
  });

  module.exports = Controller;
});

